import { useEffect, useMemo, useState, type KeyboardEvent } from "react"
import type { DateRange } from "react-day-picker"

import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar as CalendarIcon } from "@/components/icons"
import { cn } from "@/lib/utils"

// The report speaks in calendar-day strings ("YYYY-MM-DD", interpreted as UTC by
// the backend). We convert to/from local Date objects for the calendar using the
// day's own y/m/d parts — never toISOString(), which would shift across midnight.
function parseYMD(s?: string): Date | undefined {
  if (!s) return undefined
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return undefined
  return makeDate(Number(m[1]), Number(m[2]), Number(m[3]))
}

// Build a date only if the parts describe a real calendar day — the Date
// constructor silently rolls 2026-02-31 over into March, which would turn a
// typo into a valid-looking range.
function makeDate(year: number, month: number, day: number): Date | undefined {
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined
  const d = new Date(year, month - 1, day)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return undefined
  return d
}

/**
 * What the user may type. US month-first "MM/DD/YYYY" (also dotted or dashed)
 * is the house format; "YYYY-MM-DD" is still accepted so a pasted API/CSV date
 * works, and spelled-out months ("Jul 07", "August 7, 2026") are read with the
 * current year when none is given. Returns undefined for input that isn't a
 * complete, real day — the caller keeps the text and flags it rather than
 * guessing.
 *
 * Note the deliberate asymmetry: a purely numeric first field of 4 digits is
 * read as a year, everything else month-first. "07/04/2026" is July 4th, never
 * April 7th.
 */
function parseTyped(raw: string): Date | undefined {
  const s = raw.trim()
  if (!s) return undefined

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s)
  if (iso) return makeDate(Number(iso[1]), Number(iso[2]), Number(iso[3]))

  const us = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s)
  if (us) return makeDate(Number(us[3]), Number(us[1]), Number(us[2]))

  return parseNamedMonth(s)
}

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
]

function monthIndex(word: string): number | undefined {
  const w = word.toLowerCase().replace(/\.$/, "")
  if (w.length < 3) return undefined
  const i = MONTHS.findIndex((m) => m.startsWith(w))
  return i === -1 ? undefined : i + 1
}

/**
 * "Jul 07", "July 4, 2026", "7 Aug". Parsed here rather than handed to Date,
 * which reads the year-less "jul 07" as July 2007 — a silent 19-year jump in a
 * report period. A missing year means the current one.
 */
function parseNamedMonth(raw: string): Date | undefined {
  const s = raw.toLowerCase()
  const monthFirst = /^([a-z]{3,9}\.?) (\d{1,2})(?:,? (\d{4}))?$/.exec(s)
  const dayFirst = /^(\d{1,2}) ([a-z]{3,9}\.?)(?:,? (\d{4}))?$/.exec(s)
  const m = monthFirst
    ? { month: monthFirst[1], day: monthFirst[2], year: monthFirst[3] }
    : dayFirst
      ? { month: dayFirst[2], day: dayFirst[1], year: dayFirst[3] }
      : undefined
  if (!m) return undefined
  const month = monthIndex(m.month)
  if (!month) return undefined
  return makeDate(m.year ? Number(m.year) : new Date().getFullYear(), month, Number(m.day))
}

/** Wire format — what the report and the backend exchange. */
function formatYMD(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Display format — US month-first, what the fields show and re-type as. */
function formatUS(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${m}/${day}/${d.getFullYear()}`
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function addDays(d: Date, n: number): Date {
  const r = startOfDay(d)
  r.setDate(r.getDate() + n)
  return r
}

function firstOfMonth(d: Date, offsetMonths = 0): Date {
  return new Date(d.getFullYear(), d.getMonth() + offsetMonths, 1)
}

function lastOfMonth(d: Date, offsetMonths = 0): Date {
  return new Date(d.getFullYear(), d.getMonth() + offsetMonths + 1, 0)
}

/** US convention: weeks start Sunday. */
function firstOfWeek(d: Date): Date {
  const r = startOfDay(d)
  r.setDate(r.getDate() - r.getDay())
  return r
}

function firstOfQuarter(d: Date, offsetQuarters = 0): Date {
  return new Date(d.getFullYear(), (Math.floor(d.getMonth() / 3) + offsetQuarters) * 3, 1)
}

/**
 * Phrases people type instead of dates. A "day" phrase fills the one field it
 * was typed into; a "range" phrase fills both ends — "last month" is a period,
 * not a date, and splitting it across two fields would be nonsense.
 *
 * Deliberately a closed vocabulary rather than a parser: the field commits what
 * it understood as real dates, so a phrase that half-matched would silently run
 * the wrong period.
 */
type Phrase = { kind: "day"; day: Date } | { kind: "range"; from: Date; to: Date }

function resolvePhrase(raw: string): Phrase | undefined {
  const s = raw.trim().toLowerCase().replace(/\s+/g, " ")
  if (!s) return undefined
  const today = startOfDay(new Date())
  const day = (d: Date): Phrase => ({ kind: "day", day: d })
  const range = (from: Date, to: Date): Phrase => ({ kind: "range", from, to })

  switch (s) {
    case "today":
      return day(today)
    case "yesterday":
      return day(addDays(today, -1))
    case "this week":
      return range(firstOfWeek(today), today)
    case "last week": {
      const from = addDays(firstOfWeek(today), -7)
      return range(from, addDays(from, 6))
    }
    case "this month":
    case "month to date":
    case "mtd":
      return range(firstOfMonth(today), today)
    case "last month":
      return range(firstOfMonth(today, -1), lastOfMonth(today, -1))
    case "this quarter":
      return range(firstOfQuarter(today), today)
    case "last quarter":
      return range(firstOfQuarter(today, -1), lastOfMonth(firstOfQuarter(today, -1), 2))
    case "this year":
    case "year to date":
    case "ytd":
      return range(new Date(today.getFullYear(), 0, 1), today)
    case "last year":
      return range(new Date(today.getFullYear() - 1, 0, 1), new Date(today.getFullYear() - 1, 11, 31))
  }

  const ago = /^(\d{1,4}) (day|week|month|year)s? ago$/.exec(s)
  if (ago) return day(shift(today, Number(ago[1]) * -1, ago[2]))

  // "last 90 days" counts back inclusive of today, matching the presets.
  const last = /^(?:last|past) (\d{1,4}) (day|week|month|year)s?$/.exec(s)
  if (last) {
    const n = Number(last[1])
    if (n < 1) return undefined
    const from = last[2] === "day" ? addDays(today, -(n - 1)) : shift(today, -n, last[2])
    return range(from, today)
  }
  return undefined
}

function shift(d: Date, n: number, unit: string): Date {
  if (unit === "day") return addDays(d, n)
  if (unit === "week") return addDays(d, n * 7)
  if (unit === "month") return new Date(d.getFullYear(), d.getMonth() + n, d.getDate())
  return new Date(d.getFullYear() + n, d.getMonth(), d.getDate())
}

type Preset = { label: string; range: () => DateRange }

function buildPresets(): Preset[] {
  const today = startOfDay(new Date())
  return [
    { label: "Last 7 days", range: () => ({ from: addDays(today, -6), to: today }) },
    { label: "Last 31 days", range: () => ({ from: addDays(today, -30), to: today }) },
    { label: "Last 90 days", range: () => ({ from: addDays(today, -89), to: today }) },
    {
      label: "Last 6 months",
      range: () => ({
        from: new Date(today.getFullYear(), today.getMonth() - 6, today.getDate()),
        to: today,
      }),
    },
    {
      label: "Last year",
      range: () => ({
        from: new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()),
        to: today,
      }),
    },
  ]
}

type Side = "start" | "end"
type Range = { start?: string; end?: string }

export function DateRangePicker({
  start,
  end,
  onChange,
  onValidityChange,
  onSubmit,
  disabled,
  className,
}: {
  /** Inclusive range as "YYYY-MM-DD" strings; empty = default (backend picks last 31 days). */
  start?: string
  end?: string
  onChange: (start: string | undefined, end: string | undefined) => void
  /** False while a field holds text that isn't a date, so the caller can refuse to run. */
  onValidityChange?: (valid: boolean) => void
  /**
   * Enter in a date field. Carries the range the keystroke resolved to — the
   * caller's own state hasn't re-rendered yet, so reading it there would act on
   * the previous range. Not called at all when the field holds bad text.
   */
  onSubmit?: (start: string | undefined, end: string | undefined) => void
  disabled?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const presets = useMemo(buildPresets, [])

  // Text being typed, per side. Null = field mirrors the committed prop; a
  // string means the user is editing (possibly to something unparseable, which
  // we must not throw away while they're still typing).
  const [draft, setDraft] = useState<{ start: string | null; end: string | null }>({
    start: null,
    end: null,
  })
  // Raised on blur only — flagging mid-typing would paint "2026-08" red on the
  // way to a perfectly good date.
  const [rejected, setRejected] = useState<{ start: boolean; end: boolean }>({
    start: false,
    end: false,
  })
  // Left-hand month of the 2-month calendar, so it can follow what was typed.
  const [month, setMonth] = useState<Date | undefined>(undefined)

  useEffect(() => {
    onValidityChange?.(!rejected.start && !rejected.end)
  }, [rejected.start, rejected.end, onValidityChange])

  const from = parseYMD(start)
  const to = parseYMD(end)
  const selected: DateRange | undefined = from || to ? { from, to } : undefined

  // Committed values arrive as wire-format YYYY-MM-DD and are shown US-style;
  // while a field is being edited its own text wins.
  const text = (side: Side) => {
    const typed = draft[side]
    if (typed !== null) return typed
    const committed = parseYMD(side === "start" ? start : end)
    return committed ? formatUS(committed) : ""
  }

  const emit = (range: DateRange | undefined) => {
    setDraft({ start: null, end: null })
    setRejected({ start: false, end: false })
    onChange(
      range?.from ? formatYMD(range.from) : undefined,
      range?.to ? formatYMD(range.to) : undefined,
    )
  }

  // Presets / Clear apply immediately and dismiss — otherwise the open popover
  // eats the next click (radix dismisses on outside-press, so the click on
  // "Run report" only closes the popover instead of triggering it).
  const emitAndClose = (range: DateRange | undefined) => {
    emit(range)
    if (range?.from) setMonth(firstOfMonth(range.from))
    setOpen(false)
  }

  // Typed edits commit as soon as they parse, so "Run report" works without
  // leaving the field. Text that doesn't parse withdraws that end of the range
  // instead of leaving the last good keystroke committed — halfway through
  // "2026-02-31" the field has already said "2026-02-3", and that must not be
  // what the report ends up running.
  const typeInto = (side: Side, value: string) => {
    setDraft((d) => ({ ...d, [side]: value }))
    setRejected((r) => (r[side] ? { ...r, [side]: false } : r))
    const parsed = parseTyped(value)
    const ymd = parsed ? formatYMD(parsed) : undefined
    onChange(side === "start" ? ymd : start, side === "end" ? ymd : end)
    // Show the typed day: as the right-hand month when it's the range end.
    if (parsed) setMonth(firstOfMonth(parsed, side === "end" ? -1 : 0))
  }

  // On blur the field snaps back to canonical form: a parsed day is rewritten
  // as MM/DD/YYYY. Text that never parsed is kept and flagged — silently
  // reverting it would let a typo pass for the range the report actually ran.
  //
  // Phrases are resolved here rather than per keystroke: "last month" only
  // becomes a date once the user is done with it, so the field can't rewrite
  // itself out from under someone still typing.
  const settle = (side: Side): Range | null => {
    const typed = draft[side]
    const committed: Range = { start, end }
    if (typed === null) return committed
    if (typed.trim() === "" || parseTyped(typed)) {
      // typeInto already committed (or withdrew) this one; just drop the draft.
      setDraft((d) => ({ ...d, [side]: null }))
      return committed
    }
    const phrase = resolvePhrase(typed)
    if (phrase?.kind === "range") {
      emit({ from: phrase.from, to: phrase.to })
      setMonth(firstOfMonth(phrase.from))
      return { start: formatYMD(phrase.from), end: formatYMD(phrase.to) }
    }
    if (phrase?.kind === "day") {
      const ymd = formatYMD(phrase.day)
      setDraft((d) => ({ ...d, [side]: null }))
      onChange(side === "start" ? ymd : start, side === "end" ? ymd : end)
      setMonth(firstOfMonth(phrase.day, side === "end" ? -1 : 0))
      return side === "start" ? { start: ymd, end } : { start, end: ymd }
    }
    setRejected((r) => ({ ...r, [side]: true }))
    return null
  }

  const keyDown = (side: Side) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      const range = settle(side)
      if (range) onSubmit?.(range.start, range.end)
      return
    }
    // Standard combobox affordance — the calendar is one key away from the field.
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault()
      setMonth(firstOfMonth(from ?? to ?? new Date()))
      setOpen(true)
    }
  }

  const fieldClass =
    "h-7 w-[7.25rem] bg-transparent px-1.5 text-sm tabular-nums text-foreground outline-none placeholder:text-muted-foreground/70 disabled:cursor-not-allowed aria-invalid:text-destructive"
  const fieldHint =
    'MM/DD/YYYY — or a phrase like "last month", "ytd", "last 90 days"' +
    (onSubmit ? ". Enter applies the range, ↓ opens the calendar" : "")

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          "inline-flex h-8 items-center rounded-4xl border border-input bg-input/30 pr-0.5 pl-1 transition-colors focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
      >
        <input
          type="text"
          autoComplete="off"
          spellCheck={false}
          aria-label="Start date"
          aria-invalid={rejected.start || undefined}
          placeholder="MM/DD/YYYY"
          title={fieldHint}
          disabled={disabled}
          className={fieldClass}
          value={text("start")}
          onChange={(e) => typeInto("start", e.target.value)}
          onBlur={() => settle("start")}
          onKeyDown={keyDown("start")}
        />
        <span className="px-0.5 text-sm text-muted-foreground">–</span>
        <input
          type="text"
          autoComplete="off"
          spellCheck={false}
          aria-label="End date"
          aria-invalid={rejected.end || undefined}
          placeholder="MM/DD/YYYY"
          title={fieldHint}
          disabled={disabled}
          className={fieldClass}
          value={text("end")}
          onChange={(e) => typeInto("end", e.target.value)}
          onBlur={() => settle("end")}
          onKeyDown={keyDown("end")}
        />
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Open calendar"
            title="Open calendar"
            disabled={disabled}
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground focus-visible:outline-none data-[state=open]:bg-foreground/10 data-[state=open]:text-foreground"
            onClick={() => setMonth(firstOfMonth(from ?? to ?? new Date()))}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent align="start" className="flex flex-col gap-2 sm:flex-row">
        {/* Preset shortcuts */}
        <div className="flex flex-row flex-wrap gap-1 border-b border-border pb-2 sm:w-36 sm:flex-col sm:flex-nowrap sm:border-b-0 sm:border-r sm:pr-2 sm:pb-0">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              className="rounded-lg px-2.5 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              onClick={() => emitAndClose(p.range())}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className="rounded-lg px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            onClick={() => emitAndClose(undefined)}
          >
            Clear
          </button>
        </div>
        {/* Range calendar */}
        <Calendar
          mode="range"
          numberOfMonths={2}
          month={month}
          onMonthChange={setMonth}
          selected={selected}
          onSelect={(range) => {
            emit(range)
            // Close once both endpoints are chosen; stay open mid-selection.
            if (range?.from && range?.to) setOpen(false)
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )
}
